import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import styled from "styled-components";

import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import AbstractModal from "components/page/modal/AbstractModal";
import SpacePaymentExtendModal from "components/page/modal/SpacePaymentExtendModal";
import SpacePaymentNewModal from "components/page/modal/SpacePaymentNewModal";
import SpacePaymentReceiptModal from "components/page/modal/SpacePaymentReceiptModal";

import { usePaymentCoupon } from "hooks/queries/usePayment";
import { useSpaceUpdate, useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";
import { dateKr } from "utils/dateUtil";

import moment from "moment";

const Section = styled.div`
    &:not(:first-of-type) {
        margin-top: 32px;
    }
`;

const SectionTitle = styled(Text).attrs({
    withBarType: true,
    variant: "body-md-500",
    style: { display: "flex", alignItems: "center", gap: "8px" },
})``;

const SectionContents = styled.div`
    padding: ${(props) => props.padding || "16px 0 0 0"};
`;

const SubscriptionSummaryCard = styled.div`
    width: 100%;
    padding: 12px 16px;
    border-radius: 4px;
    border: 1px solid var(--color-Outline);
    background-color: var(--color-White);

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const SubscriptionLeft = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const SubscriptionRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
    white-space: nowrap;
`;

const SubscriptionPlanName = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
`;

const SubscriptionStatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;

    border-radius: 999px;
    border: 1px solid var(--color-Grey3);
    background-color: var(--color-Base1);

    font-size: 1.2rem;
    color: var(--color-SubBlack);
`;

const SubscriptionMetaText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
`;

const SubscriptionLink = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0 0 0 8px;
    font-size: 1.3rem;
    color: var(--color-SubBlack);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        opacity: 0.85;
    }
`;

const TableWrap = styled.div`
    margin: 8px 0 0 0;
    width: 100%;
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    overflow: hidden;
`;

const Table = styled.table`
    width: 100%;
    padding: 0 0 160px 0;
`;

const Tr = styled.tr`
    border-bottom: solid 1px var(--color-Outline);
    overflow: hidden;

    &:last-child {
        border-bottom: transparent;
    }
`;

const Th = styled.th`
    padding: 12px 16px;
    width: ${(props) => props.width};
    background-color: var(--color-Base1);
    text-align: ${(props) => props.textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const Td = styled.td`
    padding: ${(props) => props.padding || "12px 16px"};
    background-color: var(--color-White);
    text-align: ${(props) => props.textAlign || "center"};
    border-right: solid 1px var(--color-Grey3);

    &:last-child {
        border-right: transparent;
    }
`;

const ThText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Black)",
})``;

const TdText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})``;

const DetailRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

function PaymentInfoTab(props) {
    const { space_uuid } = useParams();

    const { setLoading } = useLoading();
    const { setToast } = useToast();

    const [user, setUser] = useState({});
    const userQuery = useUser();
    useEffect(() => {
        if (userQuery.data) {
            setUser(userQuery.data);
        }
    }, [userQuery.data]);

    const currentSpace = props.currentSpace || {};
    const spaceUpdateMutation = useSpaceUpdate();

    const [selectedPaymentHistory, setSelectedPaymentHistory] = useState({});
    const paymentHistory = props.paymentHistory || [];
    const lastPaymentHistory = props.lastPaymentHistory || null;

    // 쿠폰 관련
    const [couponList, setCouponList] = useState([]);
    const couponQuery = usePaymentCoupon();
    useEffect(() => {
        if (couponQuery.data) {
            setCouponList(couponQuery.data.filter((coupon) => !coupon.used_at));
        }
    }, [couponQuery.data]);

    // 모달
    const [showSpacePaymentNewModal, setShowSpacePaymentNewModal] = useState(false); // 신규 구독
    const [showSpacePaymentExtendModal, setShowSpacePaymentExtendModal] = useState(false); // 연장, 재구독
    const [showSpacePaymentReceiptModal, setShowSpacePaymentReceiptModal] = useState(false); // 영수증
    const [paymentExtendOnlyCoupon, setPaymentExtendOnlyCoupon] = useState(false);

    return (
        <>
            {/* 이용 현황 */}
            <Section>
                <SectionContents padding={"0"}>
                    <SubscriptionSummaryCard>
                        <SubscriptionLeft>
                            <SubscriptionPlanName>
                                {currentSpace?.plan?.name} 요금제 구독
                                {currentSpace?.status === Constants.SPACE_STATUS_ACTIVE ? " 중" : " 만료"}
                            </SubscriptionPlanName>
                            {currentSpace?.plan?.id === Constants.FREE_PLAN && (
                                <Button
                                    onlyText
                                    width={"88px"}
                                    height={"32px"}
                                    size={"small"}
                                    buttonText={"업그레이드"}
                                    bgColor={"var(--color-Button2)"}
                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                    onClick={() => {
                                        setPaymentExtendOnlyCoupon(false);
                                        setShowSpacePaymentNewModal(true);
                                    }}
                                />
                            )}
                            {lastPaymentHistory !== null &&
                                (currentSpace?.auto_extend &&
                                moment() <= moment(lastPaymentHistory.ended_at).endOf("day") ? (
                                    <>
                                        <SubscriptionStatusPill>자동 연장 중</SubscriptionStatusPill>

                                        <SubscriptionMetaText>
                                            다음 결제일:{" "}
                                            {moment(lastPaymentHistory.ended_at).add(1, "day").format("YYYY년 M월 D일")}
                                        </SubscriptionMetaText>

                                        <SubscriptionLink
                                            type="button"
                                            onClick={() => {
                                                Confirm(
                                                    "warn",
                                                    "알림",
                                                    "자동 연장을 해지하겠습니까? \n이용 기간이 만료되면 요금제가 구독 종료됩니다.",
                                                    "해지",
                                                    async () => {
                                                        try {
                                                            setLoading(true);
                                                            await spaceUpdateMutation.mutateAsync({
                                                                space_uuid,
                                                                formData: {
                                                                    auto_extend: false,
                                                                },
                                                            });
                                                            setToast("자동 연장이 해지되었습니다.", "info");
                                                        } catch (error) {
                                                            Alert(
                                                                "warn",
                                                                "에러",
                                                                error?.response?.data?.error ||
                                                                    "자동 연장 해지 중 오류가 발생했습니다.",
                                                            );
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    },
                                                );
                                            }}
                                        >
                                            해지
                                        </SubscriptionLink>
                                    </>
                                ) : (
                                    currentSpace?.scope === Constants.SPACE_SCOPE_GROUP && (
                                        <>
                                            <SubscriptionMetaText>
                                                만료일: {moment(lastPaymentHistory.ended_at).format("YYYY년 M월 D일")}
                                            </SubscriptionMetaText>

                                            <Button
                                                onlyText
                                                width={"88px"}
                                                height={"32px"}
                                                size={"small"}
                                                buttonText={
                                                    moment() <= moment(lastPaymentHistory.ended_at).endOf("day")
                                                        ? "연장하기"
                                                        : "재구독"
                                                }
                                                bgColor={"var(--color-Button2)"}
                                                hoverBgColor={"var(--color-ButtonHover2)"}
                                                onClick={() => {
                                                    setPaymentExtendOnlyCoupon(false);
                                                    setShowSpacePaymentExtendModal(true);
                                                }}
                                            />
                                        </>
                                    )
                                ))}
                        </SubscriptionLeft>
                        <SubscriptionRight>
                            {currentSpace?.scope === Constants.SPACE_SCOPE_PRIVATE && (
                                <>
                                    <SubscriptionMetaText>
                                        사용 가능한 쿠폰이 {couponList.length > 0 ? "있습니다." : "없습니다."}
                                    </SubscriptionMetaText>
                                    <Button
                                        onlyText
                                        width={"88px"}
                                        height={"32px"}
                                        size={"small"}
                                        buttonText={couponList.length > 0 ? "쿠폰 사용" : "쿠폰 등록"}
                                        bgColor={"var(--color-Button2)"}
                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                        onClick={() => {
                                            if (lastPaymentHistory) {
                                                setPaymentExtendOnlyCoupon(true);
                                                setShowSpacePaymentExtendModal(true);
                                            } else {
                                                setPaymentExtendOnlyCoupon(true);
                                                setShowSpacePaymentNewModal(true);
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </SubscriptionRight>
                    </SubscriptionSummaryCard>
                </SectionContents>
            </Section>

            {/* 이용 내역 */}
            <Section>
                <SectionTitle>이용 내역</SectionTitle>
                <SectionContents>
                    <TableWrap>
                        <Table>
                            <tbody>
                                <Tr>
                                    <Th width="160px">
                                        <ThText>일시</ThText>
                                    </Th>
                                    <Th>
                                        <ThText>상태</ThText>
                                    </Th>
                                    <Th>
                                        <ThText>결제 유형</ThText>
                                    </Th>
                                    <Th>
                                        <ThText>이용기간</ThText>
                                    </Th>
                                    <Th>
                                        <ThText>할인율</ThText>
                                    </Th>
                                    <Th>
                                        <ThText>금액</ThText>
                                    </Th>
                                    <Th>
                                        <ThText>결제수단</ThText>
                                    </Th>
                                    <Th>
                                        <ThText>영수증</ThText>
                                    </Th>
                                </Tr>
                                {paymentHistory.length === 0 ? (
                                    <Tr style={{ height: "80px" }}>
                                        <Td colSpan="9">
                                            <TdText>이용 내역이 없습니다.</TdText>
                                        </Td>
                                    </Tr>
                                ) : (
                                    paymentHistory.map((history, index) => {
                                        return (
                                            <Tr key={index}>
                                                <Td>
                                                    <TdText>
                                                        {moment(history.created_at).format("YYYY.MM.DD HH:mm")}
                                                    </TdText>
                                                </Td>
                                                <Td>
                                                    <TdText>
                                                        {history.status === "COMPLETED"
                                                            ? "결제완료"
                                                            : history.status === "FAILED"
                                                            ? "결제실패"
                                                            : history.status === "REFUND"
                                                            ? "환불"
                                                            : ""}
                                                    </TdText>
                                                </Td>
                                                <Td>
                                                    <TdText>{history.description}</TdText>
                                                </Td>
                                                <Td>
                                                    {history.started_at && history.ended_at && (
                                                        <TdText style={{ marginTop: "4px" }}>
                                                            {dateKr(moment(history.started_at))} ~{" "}
                                                            {dateKr(moment(history.ended_at))}
                                                        </TdText>
                                                    )}
                                                </Td>
                                                <Td>
                                                    <TdText>
                                                        {history?.total_discount_rate > 0
                                                            ? `${Number(history.total_discount_rate)}%`
                                                            : "-"}
                                                    </TdText>
                                                </Td>
                                                <Td textAlign="right">
                                                    {history.amount !== null && <TdText>{history.amount} 원</TdText>}
                                                </Td>
                                                <Td>
                                                    <TdText>{history.card ? "카드" : "쿠폰"}</TdText>
                                                </Td>
                                                <Td>
                                                    <DetailRow>
                                                        {history.card &&
                                                            history.amount > 0 &&
                                                            history.status === "COMPLETED" && (
                                                                <Button
                                                                    onlyText
                                                                    width={"60px"}
                                                                    height={"24px"}
                                                                    fontColor={"var(--color-White)"}
                                                                    size={"xsmall"}
                                                                    buttonText={"영수증"}
                                                                    bgColor={"var(--color-Button2)"}
                                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                                    onClick={(e) => {
                                                                        setSelectedPaymentHistory(history);
                                                                        setShowSpacePaymentReceiptModal(true);
                                                                    }}
                                                                />
                                                            )}
                                                    </DetailRow>
                                                </Td>
                                            </Tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </TableWrap>
                </SectionContents>
            </Section>
            {showSpacePaymentNewModal && (
                <AbstractModal
                    modalTitle="요금제 결제"
                    width={500}
                    height={640}
                    exitModal={(e) => {
                        setPaymentExtendOnlyCoupon(false);
                        setShowSpacePaymentNewModal(false);
                    }}
                >
                    <SpacePaymentNewModal
                        user={user}
                        isOnlyCouponUse={paymentExtendOnlyCoupon}
                        exitModal={(e) => {
                            setPaymentExtendOnlyCoupon(false);
                            setShowSpacePaymentNewModal(false);
                        }}
                    />
                </AbstractModal>
            )}
            {showSpacePaymentExtendModal && lastPaymentHistory && (
                <AbstractModal
                    modalTitle={
                        paymentExtendOnlyCoupon ? (couponList.length > 0 ? "쿠폰 사용" : "쿠폰 등록") : "이용 기간 연장"
                    }
                    width={500}
                    height={640}
                    exitModal={(e) => {
                        setPaymentExtendOnlyCoupon(false);
                        setShowSpacePaymentExtendModal(false);
                    }}
                >
                    <SpacePaymentExtendModal
                        user={user}
                        paymentHistory={lastPaymentHistory}
                        isOnlyCouponUse={paymentExtendOnlyCoupon}
                        exitModal={(e) => {
                            setPaymentExtendOnlyCoupon(false);
                            setShowSpacePaymentExtendModal(false);
                        }}
                    />
                </AbstractModal>
            )}
            {showSpacePaymentReceiptModal && (
                <AbstractModal
                    modalTitle=""
                    width={500}
                    height={640}
                    exitModal={(e) => {
                        setSelectedPaymentHistory({});
                        setShowSpacePaymentReceiptModal(false);
                    }}
                >
                    <SpacePaymentReceiptModal user={user} paymentHistory={selectedPaymentHistory} />
                </AbstractModal>
            )}
        </>
    );
}

export default PaymentInfoTab;
